import {
    format,
    add,
    eachMonthOfInterval,
    sub,
    isWithinInterval,
    formatISO,
    isValid,
    startOfMonth,
    endOfMonth,
    startOfDay,
    differenceInCalendarMonths,
    differenceInCalendarDays
} from "date-fns";

import { ptBR } from "date-fns/locale";
import _ from "lodash";

export function toLocaleDateString(date, args = []) {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString(...args);
}

function dateProcessWrapper(func) {
    return function () {
        if (!_.get(arguments, "[0]")) return null;

        arguments[0] = _processDateFormat(arguments[0]);

        if (!_checkIsValidDate(arguments[0])) return null;
        arguments[0] = _getDate(arguments[0]);

        return func.apply(this, arguments);
    };
}

/**
 * Retorna formato para guardar no banco (21:00)
 * @param {Date} date
 * @returns
 */
export function dateToDB(date) {
    const month = date.getUTCMonth() + 1;
    const _date = new Date(
        `${date.getUTCFullYear()}-${String(month).padStart(2, 0)}-01`
    );
    return _date;
}

/**
 *
 * @param {"years"|"months"|"days"|"hours"} type
 * @returns {Date}
 */
export function addToDate(date, amount, type) {
    const addToDateWrapped = dateProcessWrapper((date, amount, type) => {
        let result = add(date, { [type]: amount });
        return result;
    });

    return addToDateWrapped(date, amount, type);
}

export function dateToString(date, pattern) {
    const dateToStringWrapped = dateProcessWrapper((date, pattern) => {
        let formattedDate = format(date, pattern, { locale: ptBR });

        // workaround, o locale ptBR retorna o mês minusculo ao inves de maiusculo
        if (
            ["MMMM 'de' yyyy", "MMMM", "MMMM yyyy", "MMM, yyyy"].includes(
                pattern
            )
        )
            formattedDate = _capitalizeFirstWord(formattedDate);

        return formattedDate;
    });
    return dateToStringWrapped(date, pattern);
}

export function dateToISOString(date) {
    const dateToISOStringWrapped = dateProcessWrapper((date) => {
        return formatISO(date);
    });
    return dateToISOStringWrapped(date);
}

function _capitalizeFirstWord(string) {
    return string.replace(string[0], string[0].toUpperCase());
}

export function getPreviousMonth(date, months = 1) {
    const getPreviousMonthWrapped = dateProcessWrapper((date) => {
        return sub(date, { months });
    });
    return getPreviousMonthWrapped(date);
}

export function getNextMonth(date) {
    const getNextMonthWrapped = dateProcessWrapper((date) => {
        return add(date, { months: 1 });
    });
    return getNextMonthWrapped(date);
}

export function getMonthYearStringInterval(start, end) {
    if (!_checkIsValidDate(start) || !_checkIsValidDate(end)) {
        return null;
    }
    const monthsInterval = getMonthsInterval(start, end);
    const monthsYearsStrInterval = monthsInterval.map((month) =>
        dateToString(month, "yyyy-MM")
    );
    return monthsYearsStrInterval;
}

export function getDatesInterval(start, end) {
    if (!_checkIsValidDate(start) || !_checkIsValidDate(end)) {
        return null;
    }
    const monthsInterval = getMonthsInterval(start, end);
    const monthsYearsStrInterval = monthsInterval.map((month) =>
        getDate(new Date(month))
    );
    return monthsYearsStrInterval;
}

export function mergeDates(dates1, dates2) {
    let mergedDates = [...dates1, ...dates2].map((dt) =>
        new Date(dt).toISOString()
    );
    mergedDates = _.uniq(mergedDates);
    return mergedDates.sort();
}

export function getMonthsInterval(dirtyStartDate, dirtyEndDate) {
    if (!dirtyStartDate || !dirtyEndDate) return null;

    const isSomeDateInvalid = [dirtyStartDate, dirtyEndDate].some(
        (date) => !_checkIsValidDate(date)
    );
    if (isSomeDateInvalid) return null;

    const [startDate, endDate] = [dirtyStartDate, dirtyEndDate].map(_getDate);

    return eachMonthOfInterval({ start: startDate, end: endDate });
}

export function getStartOfMonth(date) {
    const getStartOfMonthWrapped = dateProcessWrapper((date) => {
        return startOfMonth(date);
    });
    return getStartOfMonthWrapped(date);
}

export function getEndOfMonth(date) {
    const getEndOfMonthWrapped = dateProcessWrapper((date) => {
        return endOfMonth(date);
    });
    return getEndOfMonthWrapped(date);
}

export function getDateWithoutTimeZone(date) {
    const dateWithoutTimeZone = new Date(
        date.getTime() + date.getTimezoneOffset() * 60 * 1000
    );
    return startOfDay(dateWithoutTimeZone);
}

export function isBetween(dirtyDate, dirtyStartDate, dirtyEndDate) {
    if (!dirtyDate || !dirtyStartDate || !dirtyEndDate) return null;
    [dirtyDate, dirtyStartDate, dirtyEndDate].map(_processDateFormat);
    const isSomeDateInvalid = [dirtyDate, dirtyStartDate, dirtyEndDate].some(
        (date) => !_checkIsValidDate(date)
    );
    if (isSomeDateInvalid) return false;

    const [date, startDate, endDate] = [
        dirtyDate,
        dirtyStartDate,
        dirtyEndDate
    ].map(_getDate);

    return isWithinInterval(date, { start: startDate, end: endDate });
}

export function getMonth(date) {
    const getMonthWrapped = dateProcessWrapper((date) => {
        return date.getMonth();
    });
    return getMonthWrapped(date);
}

export function getMonthIndex1(date) {
    const getMonthIndex1Wrapped = dateProcessWrapper((date) => {
        return _convertMonthTo1Index(date.getMonth());
    });
    return getMonthIndex1Wrapped(date);
}

export function getFullYear(date) {
    const getFullYearWrapped = dateProcessWrapper((date) => {
        return date.getFullYear();
    });
    return getFullYearWrapped(date);
}

export function checkIsValidDate(date) {
    if (date) return null;
    date = _processDateFormat(date);
    return isValid(new Date(date));
}

function _convertMonthTo1Index(month) {
    return Number(month) + 1;
}

function _convertMonthTo0Index(month) {
    return Number(month) - 1;
}

/**
 *
 * @returns {Date}
 */
export function getDate(date) {
    const getDateWrapped = dateProcessWrapper((date) => {
        return date;
    });
    return getDateWrapped(date);
}

function _getDateWithoutTimeZone(date) {
    if (!date) return null;

    date = _processDateFormat(date);
    if (!_checkIsValidDate(date)) return null;

    const dateWithoutTimeZone = new Date(
        date.getTime() + date.getTimezoneOffset() * 60 * 1000
    );
    return startOfDay(dateWithoutTimeZone);
}

function _checkIsValidDate(date) {
    return isValid(new Date(date));
}

function _getDate(dirtyDate) {
    const date = new Date(dirtyDate);
    if (_isISO8601(dirtyDate)) return _getDateWithoutTimeZone(date);
    else return date;
}

function _isISO8601(value) {
    const ISO8601Regex = /^(?:[1-9]\d{3}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1\d|2[0-8])|(?:0[13-9]|1[0-2])-(?:29|30)|(?:0[13578]|1[02])-31)|(?:[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00)-02-29)T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,9})?(?:Z|[+-][01]\d:[0-5]\d)$/gm;
    return ISO8601Regex.test(value);
}

function _isDateString(value) {
    const dateStringRegex = /^[0-9]{4}[-|\/]{1}[0-9]{2}([-|\/]{1}[0-9]{2})?$/gm;
    return dateStringRegex.test(value);
}

export function months(i) {
    const months = [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro"
    ];

    if (i > -1) return months[i];
    return months;
}

export function monthsByYear(years, separator = "-") {
    const entryYears = _.uniq([].concat(years));
    const allMonths = [];
    for (const year of entryYears) {
        months().forEach((m, i) => {
            const regularMonth = String(i + 1).padStart(2, "0");
            allMonths.push(
                new Date(
                    `${year}${separator}${regularMonth}${separator}01`
                ).toISOString()
            );
        });
    }
    return allMonths;
}

export function monthsAttr(i) {
    const months = [
        {
            label: "Janeiro",
            labelAbrev: "Jan",
            regularValue: 1,
            month: 1,
            stringValue: "01"
        },
        {
            label: "Fevereiro",
            labelAbrev: "Fev",
            regularValue: 2,
            month: 2,
            stringValue: "02"
        },
        {
            label: "Março",
            labelAbrev: "Mar",
            regularValue: 3,
            month: 3,
            stringValue: "03"
        },
        {
            label: "Abril",
            labelAbrev: "Abr",
            regularValue: 4,
            month: 4,
            stringValue: "04"
        },
        {
            label: "Maio",
            labelAbrev: "Mai",
            regularValue: 5,
            month: 5,
            stringValue: "05"
        },
        {
            label: "Junho",
            labelAbrev: "Jun",
            regularValue: 6,
            month: 6,
            stringValue: "06"
        },
        {
            label: "Julho",
            labelAbrev: "Jul",
            regularValue: 7,
            month: 7,
            stringValue: "07"
        },
        {
            label: "Agosto",
            labelAbrev: "Ago",
            regularValue: 8,
            month: 8,
            stringValue: "08"
        },
        {
            label: "Setembro",
            labelAbrev: "Set",
            regularValue: 9,
            month: 9,
            stringValue: "09"
        },
        {
            label: "Outubro",
            labelAbrev: "Out",
            regularValue: 10,
            month: 10,
            stringValue: "10"
        },
        {
            label: "Novembro",
            labelAbrev: "Nov",
            regularValue: 11,
            month: 11,
            stringValue: "11"
        },
        {
            label: "Dezembro",
            labelAbrev: "Dez",
            regularValue: 12,
            month: 12,
            stringValue: "12"
        }
    ];

    if (i > -1) return months[i];
    return months;
}

export function yearsAttr(years) {
    const _yearsAttr = years.map((y) => {
        return { label: y, labelAbrev: y, regularValue: 12, stringValue: "12" };
    });

    return _yearsAttr;
}

export function quartersAttr(i) {
    const quarters = [
        {
            label: "1º Trimestre",
            labelAbrev: "1º Tri.",
            regularValue: 3,
            month: 3,
            stringValue: "03"
        },
        {
            label: "2º Trimestre",
            labelAbrev: "2º Tri.",
            regularValue: 6,
            month: 6,
            stringValue: "06"
        },
        {
            label: "3º Trimestre",
            labelAbrev: "3º Tri.",
            regularValue: 9,
            month: 9,
            stringValue: "09"
        },
        {
            label: "4º Trimestre",
            labelAbrev: "4º Tri.",
            regularValue: 12,
            month: 12,
            stringValue: "12"
        }
    ];

    if (i > -1) return quarters[i];
    return quarters;
}

export function semestersAttr(i) {
    const quarters = [
        {
            label: "1º Semestre",
            labelAbrev: "1º Sem.",
            regularValue: 6,
            month: 6,
            stringValue: "06"
        },
        {
            label: "2º Semestre",
            labelAbrev: "2º Sem.",
            regularValue: 12,
            month: 12,
            stringValue: "12"
        }
    ];

    if (i > -1) return quarters[i];
    return quarters;
}

export function monthsAbrev(i) {
    const months = [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez"
    ];

    if (i > -1) return months[i];
    return months;
}

export function quarters() {
    return {
        2: "1º Trimestre",
        5: "2º Trimestre",
        8: "3º Trimestre",
        11: "4º Trimestre"
    };
}

export function getQuarters() {
    return [
        { label: "1º Trimestre", value: 1, month: 3 },
        { label: "2º Trimestre", value: 2, month: 6 },
        { label: "3º Trimestre", value: 3, month: 9 },
        { label: "4º Trimestre", value: 4, month: 12 }
    ];
}

export function semesters() {
    return {
        5: "1º Semestre",
        11: "2º Semestre"
    };
}

/**
 * @public
 * Create an array with the years to actual year
 * @param {Number} year Year to start
 * @param {Number} year Last year
 * @returns {Array} Array of years
 */
export function years(year = 2010, end = getFullYear(new Date())) {
    const years = [];

    for (year; year <= end; year++) {
        years.push(year);
    }

    return years;
}

export function getDifferenceInCalendarMonths(dirtyLeftDate, dirtyRightDate) {
    if (!dirtyLeftDate || !dirtyRightDate) return null;
    [dirtyLeftDate, dirtyRightDate] = [dirtyLeftDate, dirtyRightDate].map(
        _processDateFormat
    );
    const isSomeDateInvalid = [dirtyLeftDate, dirtyRightDate].some(
        (date) => !_checkIsValidDate(date)
    );

    if (isSomeDateInvalid) return false;

    const [leftDate, rightDate] = [dirtyLeftDate, dirtyRightDate].map(_getDate);

    return differenceInCalendarMonths(leftDate, rightDate);
}

export function getDifferenceInCalendarDays(dirtyLeftDate, dirtyRightDate) {
    if (!dirtyLeftDate || !dirtyRightDate) return null;
    [dirtyLeftDate, dirtyRightDate] = [dirtyLeftDate, dirtyRightDate].map(
        _processDateFormat
    );
    const isSomeDateInvalid = [dirtyLeftDate, dirtyRightDate].some(
        (date) => !_checkIsValidDate(date)
    );

    if (isSomeDateInvalid) return false;

    const [leftDate, rightDate] = [dirtyLeftDate, dirtyRightDate].map(_getDate);

    return differenceInCalendarDays(leftDate, rightDate);
}

function _processDateFormat(dateFormat) {
    if (Array.isArray(dateFormat) && (dateFormat[1] === 0 || dateFormat[1]))
        dateFormat[1] = _convertMonthTo1Index(dateFormat[1]);
    else if (typeof dateFormat === "string" && _isDateString(dateFormat))
        dateFormat = _processMonthString(dateFormat);

    return dateFormat;
}

function _processMonthString(dateFormat) {
    const year = Number(dateFormat.substr(0, 4));
    const month = Number(dateFormat.substr(5, 2));
    return `${year}/${month}/1`;
}

/**
 * @public
 * Indentify the mutipliers by kind of period
 * @param {String} key Period
 * @returns {Object || Number} Multiplier or all object
 */
export function multipliers(key) {
    const multipliers = {
        monthly: 1,
        quarterly: 3,
        semester: 6,
        yearly: 12
    };

    return key ? multipliers[key] : multipliers;
}

export function periodToHumanRead(key) {
    const multipliers = {
        monthly: "Mensal",
        quarterly: "Trimestral",
        semester: "Semestral",
        yearly: "Anual"
    };

    return key ? multipliers[key] : multipliers;
}

/**
 * @public
 * Get the labels of the period
 * @param {"previous" | "next"} type Type of period
 * @returns {Object} Labels
 */
export function labels(type = "previous") {
    const labels = {
        previous: {
            monthly: "Mês Anterior",
            quarterly: "Trimestre Anterior",
            semester: "Semestre Anterior",
            yearly: "Ano Anterior",
            other: "Outro Período"
        },
        next: {
            monthly: "Próximo Mês",
            quarterly: "Próximo Trimestre",
            semster: "Próximo Semestre",
            yearly: "Próximo Ano",
            other: "Outro Período"
        }
    };

    return labels[type];
}

/**
 * @public
 * Transform the date setted in format to use in URL
 * @returns {String} Date formatted
 */
export function toUrl(date) {
    const toUrlWrapped = dateProcessWrapper((date) => {
        return dateToString(date, "yyyy-MM");
    });
    return toUrlWrapped(date);
}

/**
 * @public
 * Identify the corret label format by kind of the period
 * @param {"monthly" | "quarterly" | "yearly"} key Period
 * @param {Boolean} abrev If want a abreviate mode
 * @returns {String || Object} The correct format or all the object
 */
export function labelFormat(key, abrev, semester) {
    const periods = {
        monthly: `${abrev ? "MMM’yy" : "MMMM 'de' yyyy"}`,
        quarterly: `Q'º' ${abrev ? "'Tri'’yy" : "'Trimestre de' yyyy"}`,
        semester: `${semester}'º' ${
            abrev ? "'Sem.'’yy" : "'Semestre de' yyyy"
        }`,
        yearly: "yyyy"
    };

    return key ? periods[key] : periods;
}

/**
 * @private
 * Retona o nome de um Quarterly de acordo com uma data
 * @returns {String}
 */
export function nameQuarterly(date, abrev = false) {
    const quarterly = getMonth(date);
    const quarterlys = quarters();
    const result = quarterlys[quarterly];
    if (abrev && result) return result.substring(0, 6);
    return result;
}

/**
 * @private
 * Retona o nome de um Quarterly de acordo com uma data
 * @returns {String}
 */
export function nameSemester(date, abrev = false) {
    const semester = getMonth(date);
    const semest = semesters();
    const result = semest[semester];
    if (abrev && result) return result.substring(0, 6);
    return result;
}

/**
 * @public
 * Retorna a data formattada em string de acorod com o periodo
 * @param {String} period
 * @param {Date} date
 * @returns
 */
export function getDateStringFormatted(period, date) {
    if (period === "semester") {
        const quarter = dateToString(date, "Q");
        const semester = parseInt(quarter) / 2;
        return dateToString(date, labelFormat(period, false, semester));
    }

    return dateToString(date, labelFormat(period));
}

/**
 * @public
 * Retorna o label da de acordo com a comparacao
 * @param {Object} period
 * @param {Object} comparation
 * @returns
 */
export function getComparationLabel(period, comparation) {
    const comparationPeriod = comparation.period;
    if (comparationPeriod == period.period) {
        const diff = getDifferenceInCalendarMonths(
            period.date,
            comparation.date
        );
        const multiplier = multipliers()[comparationPeriod];

        if (multiplier == diff) {
            return labels()[comparationPeriod];
        }
    }
    return getDateStringFormatted(comparation.period, comparation.date);
}

export function addDate(typeDate, number) {
    const date = new Date();
    const types = {
        days: (number) => [
            date.getFullYear(),
            date.getMonth(),
            date.getDate() + number
        ],
        months: (number) => [
            date.getFullYear(),
            date.getMonth() + number,
            date.getDate()
        ],
        years: (number) => [
            date.getFullYear() + number,
            date.getMonth(),
            date.getDate()
        ]
    };

    const dateAdded = new Date(...types[typeDate](number));
    return dateAdded;
}

export function elapsed(startDate, endDate = new Date()) {
    let timeDiff = endDate - startDate;
    timeDiff /= 1000;

    let seconds = Math.round(timeDiff % 60);
    timeDiff = Math.floor(timeDiff / 60);

    let minutes = Math.round(timeDiff % 60);
    timeDiff = Math.floor(timeDiff / 60);

    let hours = Math.round(timeDiff % 24);
    timeDiff = Math.floor(timeDiff / 24);

    let days = timeDiff;

    return { days, hours, minutes };
}

export function getRegularMonth(date = new Date()) {
    const month = date.getUTCMonth() + 1;
    const regularMonth = String(month).padStart(2, "0");

    return regularMonth;
}

export function gerarIntervaloMeses(dataInicial, dataFinal) {
    var meses = [];
    var dataAtual = new Date(dataInicial);
    var dataFinal = new Date(dataFinal);

    while (dataAtual <= dataFinal) {
        var mes = dataAtual.getUTCMonth() + 1; // Mês começa de zero, então adicionamos 1
        var ano = dataAtual.getUTCFullYear();
        meses.push(
            new Date(`${ano}-${String(mes).padStart(2, "0")}-01`).toISOString()
        );
        dataAtual.setUTCMonth(dataAtual.getUTCMonth() + 1);
    }

    return meses;
}

export function generateDatesInterval(dateStart, dateEnd) {
    var meses = [];
    var currentDate = new Date(dateStart);
    var dateEnd = new Date(dateEnd);

    while (currentDate <= dateEnd) {
        var mes = currentDate.getUTCMonth() + 1;
        var ano = currentDate.getUTCFullYear();
        meses.push(
            new Date(`${ano}-${String(mes).padStart(2, "0")}-01`).toISOString()
        );
        currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
    }

    return meses;
}

export function generateNewMonths(dateStrings, numMonths, onlyNewValues) {
    if (!dateStrings.length) return [];
    const dates = dateStrings.map((dateString) => new Date(dateString));
    const sortedDates = dates.sort((a, b) => +a - +b);
    const lastDate = sortedDates[sortedDates.length - 1];
    const newDates = Array.from({ length: numMonths }, (_, index) => {
        return new Date(
            lastDate.getUTCFullYear(),
            lastDate.getUTCMonth() + index + 1,
            1
        ).toISOString();
    });

    if (onlyNewValues) {
        return newDates;
    }

    return [...dates, ...newDates];
}
